import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";
import ArticleCard from "@/components/articles/ArticleCard";
import { fetchArticle, fetchArticles } from "@/lib/api";

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${d.toLocaleString("en-GB", { month: "long" })} ${d.getFullYear()}`;
};

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (data) => {
  let el = document.getElementById("article-jsonld");
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "article-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [dialog, setDialog] = useState({ open: false, type: "meeting" });
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setNotFound(false);
    fetchArticle(slug)
      .then((a) => {
        if (!alive) return;
        setArticle(a); setLoading(false);
        return fetchArticles({ limit: 6 });
      })
      .then((list) => {
        if (!alive || !list) return;
        setRelated(list.filter((x) => x.slug !== slug).slice(0, 3));
      })
      .catch(() => { if (alive) { setNotFound(true); setLoading(false); } });
    window.scrollTo(0, 0);
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const title = (article.seo_title && article.seo_title.trim()) || `${article.title} | Utsah`;
    const description = (article.meta_description && article.meta_description.trim()) || article.excerpt || "";
    const image = assetUrl(article.featured_image);
    const url = window.location.href;
    document.title = title;
    document.querySelectorAll('meta[name="description"]').forEach((el) => el.setAttribute("content", description));
    setMeta("og:type", "article", "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    if (image) setMeta("og:image", image, "property");
    setMeta("og:url", url, "property");
    setCanonical(url);
    setJsonLd({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": description,
      "image": image || undefined,
      "datePublished": article.publish_date || article.created_at,
      "dateModified": article.updated_at || article.publish_date || article.created_at,
      "author": { "@type": "Organization", "name": "Utsah" },
      "publisher": { "@type": "Organization", "name": "Utsah" },
      "mainEntityOfPage": { "@type": "WebPage", "@id": url },
    });
  }, [article]);

  if (loading) {
    return (
      <div className="utsah-app">
        <Nav onBookMeeting={openMeeting} />
        <div className="section" style={{ minHeight: "50vh", display: "grid", placeItems: "center", opacity: 0.6, fontStyle: "italic" }}>Loading…</div>
        <Footer handlers={handlers} />
      </div>
    );
  }
  if (notFound || !article) return <Navigate to="/resources" replace />;

  const dateStr = formatDate(article.publish_date || article.created_at);

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />

      <section className="section article-hero" data-testid="article-hero" style={{ paddingTop: 40 }}>
        <div className="section-inner" style={{ maxWidth: 820, margin: "0 auto" }}>
          <div className="article-breadcrumb">
            <Link to="/resources">← Insights &amp; Resources</Link>
          </div>
          {article.category && <Reveal className="eyebrow" style={{ marginTop: 24 }}>{article.category}</Reveal>}
          <Reveal as="h1" delay={1} className="section-headline article-title" data-testid="article-title">
            {article.title}
          </Reveal>
          <Reveal delay={2} className="article-meta" data-testid="article-meta">
            {dateStr && <span>{dateStr}</span>}
            {article.reading_time && <span>{article.reading_time} min read</span>}
          </Reveal>
        </div>
      </section>

      {article.featured_image && (
        <section className="section article-cover" data-testid="article-cover">
          <div className="section-inner" style={{ maxWidth: 960, margin: "0 auto" }}>
            <img src={assetUrl(article.featured_image)} alt={article.title} />
          </div>
        </section>
      )}

      <section className="section article-body-section" data-testid="article-body-section">
        <div className="section-inner article-content" data-testid="article-content"
          dangerouslySetInnerHTML={{ __html: article.content || "" }}
        />
      </section>

      {related.length > 0 && (
        <section className="section article-related" data-testid="article-related">
          <div className="section-inner">
            <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Continue Reading</Reveal>
            <Reveal as="h2" delay={1} className="section-headline" style={{ textAlign: "center" }}>
              Related <em>Articles.</em>
            </Reveal>
            <div className="article-grid" style={{ marginTop: 48 }}>
              {related.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          </div>
        </section>
      )}

      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source={`Article · ${article.title}`} onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}
